// Soft Depth design tokens — the one place their values live in code.
//
// Mirrors the published design system (tokens.json in the "PortfoPlus Soft
// Depth" artifact). The values were consolidated from ClientV2's styles.scss:
// the accent families are its --cv2-accent-* tokens, the neutrals its slate
// greys. Components never read this file directly — they use theme keys
// (`primary.main`, `text.secondary`) through theme.ts, so an AI or a person
// writing a screen picks names, never hex codes.

export type SoftDepthMode = 'business' | 'recruit';

export type AccentFamily = {
  main: string;
  strong: string;
  deep: string;
  muted: string;
  border: string;
  hair: string;
  soft: string;
  page: string;
  /** rgb triplet of `strong`, for the contained-button shadow. */
  shadowRgb: string;
};

export const ACCENT: Record<SoftDepthMode, AccentFamily> = {
  business: {
    main: '#3966f8',
    strong: '#264be8',
    deep: '#1f38d4',
    muted: '#96b3fc',
    border: '#c1d1fe',
    hair: '#dce5fe',
    soft: '#eef3ff',
    page: '#f6f8fc',
    shadowRgb: '38, 75, 232'
  },
  // 招募模式: only the accent family and the page ground change; greys, cards
  // and the app header stay put (see the recruit theme in ClientV2 styles.scss).
  recruit: {
    main: '#b45309',
    strong: '#92400e',
    deep: '#78350f',
    muted: '#e0b083',
    border: '#f5dfc0',
    hair: '#f5dfc0',
    soft: '#fef3e2',
    page: '#fffaf2',
    shadowRgb: '146, 64, 14'
  }
};

export const NEUTRAL = {
  surface: '#ffffff',
  fill: '#f1f5f9',
  fillSoft: '#f8fafc',
  line: '#e2e8f0',
  lineSoft: '#eef2f7',
  inkStrong: '#0f172a',
  inkHeading: '#1f2a44',
  ink: '#334155',
  inkSecondary: '#475569',
  // The lightest grey allowed for text (4.76:1 on surface).
  inkMuted: '#64748b',
  // Placeholder / disabled / decorative only: 2.56:1 on surface.
  inkFaint: '#94a3b8'
};

// `border` outlines a status-tinted chip or panel (the -200 step of each hue).
export const STATUS = {
  success: { main: '#059669', text: '#047857', soft: '#ecfdf5', border: '#a7f3d0' },
  warning: { main: '#d97706', text: '#9a3412', soft: '#fff7ed', border: '#fed7aa' },
  danger: { main: '#dc2626', text: '#b91c1c', soft: '#fef2f2', border: '#fecaca' },
  // Reserved for AI-generated content and AI actions.
  ai: { main: '#7c3aed', text: '#6d28d9', soft: '#f5f3ff', border: '#ddd6fe' }
};

// The adviser's own notes (Smart IDEA's 筆記): paper yellow, carried over from
// the pre-Soft-Depth dialog so advisers still spot their notes at a glance.
// Not a status colour: it means "your note", never "warning".
export const NOTE = { main: '#d97706', text: '#451a03', soft: '#fffbeb', border: '#fef3c7' };

// Category hues: tell kinds of thing apart (Smart IDEA's 婚姻 / 性別 / 子女
// tags, one hue per 保障 type), carried over from the pre-Soft-Depth dialog.
// They name a category, never a state, and stay the same in both modes.
export const HUE = {
  blue: { main: '#3b82f6', text: '#1d4ed8', soft: '#eff6ff', border: '#bfdbfe' },
  violet: { main: '#8b5cf6', text: '#6d28d9', soft: '#f5f3ff', border: '#ddd6fe' },
  emerald: { main: '#10b981', text: '#047857', soft: '#ecfdf5', border: '#a7f3d0' },
  pink: { main: '#ec4899', text: '#be185d', soft: '#fdf2f8', border: '#fbcfe8' },
  amber: { main: '#f59e0b', text: '#b45309', soft: '#fffbeb', border: '#fde68a' },
  cyan: { main: '#06b6d4', text: '#0e7490', soft: '#ecfeff', border: '#a5f3fc' },
  indigo: { main: '#6366f1', text: '#4338ca', soft: '#eef2ff', border: '#c7d2fe' }
};

export type HueName = keyof typeof HUE;

// 潛力指數 (CPI) signal colours, unchanged from V1 (ClientEntry.js
// BORDER_COLORS, mirrored in ClientV2's clientPresentation.ts): advisers read
// them as the same three lights on every screen, so they are not remapped onto
// the status colours. Decorative dots only — always beside their word.
export const CPI = {
  high: '#5EE092',
  good: '#FFAF19',
  fair: '#FF6A74'
};

// 支票精靈「支票版」: a printed cheque, so its paper and pen read as paper and pen
// rather than as UI. Not a status and not the accent; the same in both modes.
export const CHEQUE = {
  paper: '#fbf8ef',
  edge: '#e9dfc4',
  guilloche: '#d9cba4',
  print: '#6f6247',
  rule: '#c8b98f',
  ink: '#1e3a8a'
};

export const RADIUS = {
  xs: 4,
  sm: 8,
  md: 12,
  lg: 16,
  xl: 20,
  pill: 999
};

export const SHADOW = {
  card: '0 2px 10px rgba(31, 42, 68, 0.055), 0 1px 2px rgba(31, 42, 68, 0.04)',
  hover: '0 4px 12px rgba(31, 42, 68, 0.07)',
  pop: '0 8px 20px rgba(31, 42, 68, 0.09)',
  accent: (mode: SoftDepthMode) => `0 4px 10px rgba(${ACCENT[mode].shadowRgb}, 0.25)`
};

// A display line the adviser can step up or down with the page's A− / A+ control
// — the cheque helper's payee name and amount in words, which a client reads
// across the table while writing the cheque. Four steps, so a screen picks a
// step instead of writing a size of its own.
export const DISPLAY_STEPS = [
  { fontSize: '18px', lineHeight: '26px' },
  { fontSize: '23px', lineHeight: '32px' },
  { fontSize: '29px', lineHeight: '38px' },
  { fontSize: '36px', lineHeight: '46px' }
] as const;

export const DISPLAY_VARIANTS = ['display1', 'display2', 'display3', 'display4'] as const;

export type DisplayVariant = (typeof DISPLAY_VARIANTS)[number];

export const FONT_FAMILY = "Roboto, 'Noto Sans TC', 'Noto Sans SC', sans-serif";
